<template>
  <div class="event-home-container" v-if="loaded">
    <div class="about-container">
      <p class="section-title"><b>About this event</b></p>
      <p class="event-count" v-if="eventData.tour.events.length > 1">
        <b
          >Part of
          <router-link
            :to="{
              name: 'tournamentoverview-home',
              params: { tourId: eventData.tour.id },
            }"
            >{{ eventData.tour.name }}</router-link
          ></b
        >
        (event {{ eventNumberInTour }} of {{ eventData.tour.events.length }})
      </p>

      <div
        class="event-description-preview-container"
        :class="{ 'is-truncated': isDescriptionTruncated }"
        ref="eventDescriptionPreview"
      >
        <vue-markdown
          ref="eventDescriptionPreviewContent"
          class="event-description-preview"
          v-if="eventData.description"
          :source="eventData.description"
        />
      </div>
      <p
        class="read-more"
        v-if="isDescriptionTruncated"
        @click="showReadMorePopup = true"
      >
        Read more
      </p>

      <div
        class="labels"
        v-if="
          !eventData.singles ||
          eventData.tour.type ||
          eventData.pdgaType ||
          eventData.maxWildcards ||
          eventData.scoringAs === 'RANDOM_DOUBLES'
        "
      >
        <span class="doubles" v-if="eventData.scoringAs === 'RANDOM_DOUBLES'"
          ><b>Random doubles</b></span
        >
        <span
          class="doubles"
          v-else-if="!eventData.singles && eventData.teamSize === 2"
          ><b>Doubles</b></span
        >
        <span
          class="doubles"
          v-else-if="!eventData.singles && eventData.teamSize > 2"
          ><b>{{ eventData.teamSize }}-Player teams </b></span
        >
        <span class="tour-type" v-if="eventData.tour.type"
          ><b>{{ eventData.tour.type.name }}</b></span
        >
        <span class="pdga-tier" v-if="eventData.pdgaType"
          ><b>{{ eventData.pdgaType.name }}</b></span
        >
        <span class="max-wildcards" v-if="eventData.maxWildcards"
          ><b
            >{{ eventData.maxWildcards }}
            {{ eventData.maxWildcards === 1 ? 'Wildcard' : 'Wildcards' }}</b
          ></span
        >
      </div>
      <ul class="info-sections-list">
        <li
          v-for="section in eventData.infoSections"
          @click="
            showInfoSectionPopup = true
            infoSectionToShow = section
          "
        >
          <p>
            <b>{{ section.title }}</b>
          </p>
          <span><Chevron2 direction="right" /></span>
        </li>

        <li v-for="section in eventData.tourInfoSections">
          <p>
            <b>{{ section.title }}</b>
          </p>
          <span
            @click="
              showInfoSectionPopup = true
              infoSectionToShow = section
            "
            >Read more</span
          >
        </li>
      </ul>
    </div>
    <div class="location-container" v-if="eventData.city">
      <span>
        <CalendarColored />
        <p>
          Starts
          <b>{{ dateFormatter(eventData.startDate, 'D MMM YYYY HH:mm') }}</b>
        </p>
      </span>
      <span>
        <LocationColored />
        <p>
          Played in
          <b>
            <a
              target="_blank"
              :href="
                'https://www.google.com/maps/@?api=1&map_action=map&zoom=12&center=' +
                eventData.city.geolocation.lat +
                ',' +
                eventData.city.geolocation.lng
              "
              >{{ eventData.city.name }}, {{ eventData.city.country }}</a
            >
          </b>
        </p>
      </span>
    </div>
    <div class="schedule-container">
      <p class="section-title"><b>Event schedule</b></p>
      <TheEventSchedule
        :event="eventData"
        v-if="eventData.type === 'DISCGOLF'"
      />
      <TheEventGameSchedule :event="eventData" v-else />
    </div>

    <div class="td-container" v-if="showTdFields || showAssTdFields">
      <div class="td-wrapper" v-if="showTdFields">
        <p class="td-title"><b>The Tournament Director</b></p>
        <p class="td-name" v-if="eventData.tournamentDirectorName">
          <b>{{ eventData.tournamentDirectorName }}</b>
        </p>
        <div class="td-phone" v-if="eventData.phone">
          <Mobile />
          <p>{{ eventData.phone }}</p>
        </div>
        <div class="td-email" v-if="eventData.email">
          <Messages /><a :href="'mailto:' + eventData.email">Email</a>
        </div>
      </div>
      <div class="td-wrapper assistant" v-if="showAssTdFields">
        <p class="td-title"><b>Assistant Tournament Director</b></p>
        <p class="td-name" v-if="eventData.assistantTournamentDirectorName">
          <b>{{ eventData.assistantTournamentDirectorName }}</b>
        </p>
        <div class="td-phone" v-if="eventData.assistantTournamentDirectorPhone">
          <Mobile />
          <p>{{ eventData.assistantTournamentDirectorPhone }}</p>
        </div>
        <div class="td-email" v-if="eventData.assistantTournamentDirectorEmail">
          <Messages /><a
            :href="'mailto:' + eventData.assistantTournamentDirectorEmail"
            >Email</a
          >
        </div>
      </div>
    </div>
    <div
      class="website-pdga-container"
      v-if="eventData.website || eventData.pdgaEventId"
    >
      <a :href="eventData.website" target="_blank" v-if="eventData.website"
        >Website</a
      >
      <a
        v-if="eventData.pdgaEventId"
        :href="'https://www.pdga.com/tour/event/' + eventData.pdgaEventId"
        target="_blank"
        >PDGA event page</a
      >
    </div>
    <div class="divisions-container">
      <div class="divisions">
        <p class="section-title"><b>Available divisions</b></p>
        <div
          class="division"
          v-for="division in eventData.divisions"
          @click="openDivisionPopup(division)"
        >
          <p class="division-name">
            <b>{{ division.name }}</b>
          </p>
          <div
            class="cost-label"
            v-if="
              division.registrationFee && division.registrationFee.value > 0
            "
          >
            <p>
              <b>{{
                formatCurrency(
                  division.registrationFee.currency,
                  division.registrationFee.value
                )
              }}</b>
            </p>
          </div>
          <div class="cost-label free" v-else>
            <p>
              <b>Free</b>
            </p>
          </div>
          <div class="tour-pass" @click.prevent.stop>
            <TjingTextWrapTooltip :unique="division.id">
              <template v-slot:tooltip>
                <p
                  v-if="division.tourPassFee || division.tourPasses.length > 0"
                >
                  <b>Tourpass available</b>
                </p>
                <p v-else><b>Tourpass not available</b></p>
              </template>
              <template v-slot:content>
                <Events
                  :coloured="true"
                  :class="{
                    disabled:
                      !division.tourPassFee && division.tourPasses.length === 0,
                  }"
                />
              </template>
            </TjingTextWrapTooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="documents-container" v-if="eventData.documents.length > 0">
      <p class="section-title"><b>Useful documents</b></p>
      <div class="documents-wrapper" v-for="document in eventData.documents">
        <div class="document" @click="downloadDocument(document)">
          <p class="document-name">{{ document.displayName }}</p>
          <p class="document-type">{{ getFileEndingFromURL(document.url) }}</p>
          <div class="download">
            <DocumentDownload />
          </div>
        </div>
      </div>
    </div>
    <div
      class="scoring-method-container"
      v-if="
        eventData.type === 'DISCGOLF' &&
        (eventData.isVerifyMode ||
          eventData.isResultSigningEnabled ||
          eventData.multipleScorecardModeEnabled)
      "
    >
      <p class="section-title"><b>The scoring method</b></p>
      <div class="scoring-method-wrapper" v-if="eventData.isVerifyMode">
        <div class="scoring-method-title">
          <FeatureVerifier />
          <p><b>The Verifier</b></p>
        </div>
        <p class="scoring-method-copy">
          To maintain the integrity of the game, one player from each group will
          review and verify the recorded scores after each hole.
        </p>
      </div>
      <div
        class="scoring-method-wrapper"
        v-if="eventData.isResultSigningEnabled"
      >
        <div class="scoring-method-title">
          <FeatureSignature />
          <p><b>The Signature</b></p>
        </div>
        <p class="scoring-method-copy">
          At the end of each round, players must sign off on their final score
          using their secure PIN within the app.
        </p>
      </div>

      <div
        class="scoring-method-wrapper"
        v-if="eventData.multipleScorecardModeEnabled"
      >
        <div class="scoring-method-title">
          <FeatureGroupScoring />
          <p><b>Group Scoring</b></p>
        </div>
        <p class="scoring-method-copy">
          Each player is responsible for taking scores for all players in their
          group. At the end of the round, scores are automatically synced and
          cross-checked for any discrepancies, ensuring that everyone is in
          agreement before finalising the results.
        </p>
      </div>
    </div>
    <div
      class="sponsors-container"
      v-if="eventData.sponsors && eventData.sponsors.length > 0"
    >
      <p class="section-title"><b>Event Sponsors</b></p>
      <a
        :href="sponsor.url"
        target="_blank"
        v-for="sponsor in eventData.sponsors"
        :key="sponsor.id"
      >
        <img
          :src="sponsor.imageUrl"
          alt="Sponsor logo"
          v-if="sponsor.imageUrl"
        />
        <div class="logo-placeholder" v-else></div>
      </a>
    </div>
    <div
      class="sponsors-container"
      v-if="eventData.tour.sponsors && eventData.tour.sponsors.length > 0"
    >
      <p class="section-title"><b>Tour Sponsors</b></p>
      <a
        :href="sponsor.url"
        target="_blank"
        v-for="sponsor in eventData.tour.sponsors"
        :key="sponsor.id"
      >
        <img
          :src="sponsor.imageUrl"
          alt="Sponsor logo"
          v-if="sponsor.imageUrl"
        />
        <div class="logo-placeholder" v-else></div>
      </a>
    </div>
    <div
      class="organizers-container"
      v-if="eventData.tour.organizations.length > 0"
    >
      <p class="section-title"><b>Event Organisers</b></p>
      <div
        class="organizer-wrapper"
        @click="goToOrganizer(organizer)"
        v-for="organizer in eventData.tour.organizations"
      >
        <img
          v-if="organizer.logoUrl"
          :src="organizer.logoUrl"
          alt=""
          class="organizer-logo"
        />
        <div class="organizer-logo-placeholder" v-else></div>
        <p>
          <b>{{ organizer.name }}</b>
        </p>
        <div class="organizer-label club" v-if="organizer.type === 'CLUB'">
          <b>Club</b>
        </div>
        <div
          class="organizer-label promoter"
          v-if="organizer.type === 'BUSINESS'"
        >
          <b>Promoter</b>
        </div>
        <div
          class="organizer-label association"
          v-if="organizer.type === 'ASSOCIATION'"
        >
          <b>Association</b>
        </div>
      </div>
    </div>

    <TjingPopup :show="showReadMorePopup" @close="showReadMorePopup = false">
      <div class="read-more-container">
        <p class="section-title"><b>About this event</b></p>
        <p class="event-description">
          <vue-markdown :source="eventData.description" />
        </p>
      </div>
    </TjingPopup>

    <TjingPopup
      :show="showInfoSectionPopup"
      @close="showInfoSectionPopup = false"
    >
      <div class="read-more-container" v-if="infoSectionToShow">
        <p class="section-title">
          <b>{{ infoSectionToShow.title }}</b>
        </p>
        <p class="event-description">
          <vue-markdown :source="infoSectionToShow.content" />
        </p>
      </div>
    </TjingPopup>
    <TjingPopup
      ref="divisionPopup"
      :show="showDivisionPopup"
      @close="showDivisionPopup = false"
    >
      <DivisionDetailsPopup
        :division="divisionToShow"
        :stages="eventData.registrationStages"
      />
    </TjingPopup>
  </div>
</template>

<script>
import CalendarColored from '@/assets/icons/CalendarColored.vue'
import LocationColored from '@/assets/icons/LocationColored.vue'
import DivisionDetailsPopup from '@/components/DivisionDetailsPopup.vue'
import TheEventSchedule from '@/components/TheEventSchedule.vue'
import TheEventGameSchedule from '@/components/TheEventGameSchedule.vue'
import FeatureSignature from '@/assets/icons/FeatureSignature.vue'
import FeatureGroupScoring from '@/assets/icons/FeatureGroupScoring.vue'
import FeatureVerifier from '@/assets/icons/FeatureVerifier.vue'
import DocumentDownload from '@/assets/icons/DocumentDownload.vue'
import Chevron2 from '@/assets/icons/Chevron2.vue'
import Events from '@/assets/icons/Events'
import Messages from '@/assets/icons/Messages2.vue'
import Mobile from '@/assets/icons/Mobile.vue'
import TjingTextWrapTooltip from '@/components/UIElements/TjingTextWrapTooltip'
import dateFormats from '@/mixins/dateFormats.js'
import VueMarkdown from 'vue-markdown'
export default {
  name: 'PublicEventHome',
  components: {
    VueMarkdown,
    CalendarColored,
    LocationColored,
    DivisionDetailsPopup,
    TheEventGameSchedule,
    TheEventSchedule,
    FeatureSignature,
    FeatureGroupScoring,
    FeatureVerifier,
    DocumentDownload,
    Events,
    Messages,
    Mobile,
    TjingTextWrapTooltip,
    Chevron2,
  },
  mixins: [dateFormats],
  data() {
    return {
      showInfoSectionPopup: false,
      infoSectionToShow: null,
      eventData: null,
      loaded: false,
      isDescriptionTruncated: false,
      showReadMorePopup: false,
      showDivisionPopup: false,
      divisionToShow: null,
    }
  },
  computed: {
    eventNumberInTour() {
      let counter = 0
      this.eventData.tour.events.forEach((event, index) => {
        if (event.id == this.eventData.id) counter = index + 1
      })

      return counter
    },
    showTdFields() {
      return (
        this.eventData.tournamentDirectorName ||
        this.eventData.phone ||
        this.eventData.email
      )
    },
    showAssTdFields() {
      return (
        this.eventData.assistantTournamentDirectorName ||
        this.eventData.assistantTournamentDirectorPhone ||
        this.eventData.assistantTournamentDirectorEmail
      )
    },
  },
  methods: {
    formatCurrency(currencyCode, value) {
      return new Intl.NumberFormat(navigator.language, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
      }).format(value)
    },
    openDivisionPopup(division) {
      this.divisionToShow = division
      this.showDivisionPopup = true
    },
    goToOrganizer(org) {
      if (org.type === 'CLUB') {
        this.$router.push({
          name: 'club',
          params: { slug: org.slug, scrollToTop: true },
        })
      } else if (org.type === 'BUSINESS') {
        this.$router.push({
          name: 'promoter',
          params: { slug: org.slug, scrollToTop: true },
        })
      } else if (org.type === 'ASSOCIATION') {
        this.$router.push({
          name: 'association',
          params: { slug: org.slug, scrollToTop: true },
        })
      }
    },
    getFileEndingFromURL(url) {
      return url.split(/[#?]/)[0].split('.').pop().trim()
    },
    downloadDocument(file) {
      let downloadUrl = file.url

      if (downloadUrl.includes('/upload/')) {
        downloadUrl = downloadUrl.replace(
          '/upload/',
          `/upload/fl_attachment:${file.displayName.replace(
            /[^a-zA-Z0-9-_]/g,
            ''
          )}/`
        )
      }
      const link = document.createElement('a')
      link.href = downloadUrl
      link.click()
    },
    showDivisionDetails(division) {
      return (
        division.maxRegistrations ||
        division.reservedSpots ||
        division.maxSpots ||
        division.refundPercentage !== 100 ||
        this.eventData.registrationStages.length > 0
      )
    },
    checkTruncation() {
      this.$nextTick(() => {
        const description = this.$refs.eventDescriptionPreview

        if (
          description &&
          description.scrollHeight > description.clientHeight
        ) {
          this.isDescriptionTruncated = true
        } else {
          this.isDescriptionTruncated = false
        }
      })
    },
    async loadData() {
      const eventData = await this.$axios({
        data: {
          query: `
          {
            event(eventId: "${this.$router.currentRoute.params.eventId}") {
              id
              type
              singles
              description
              scoringAs
              teamSize
              tournamentDirectorName
              phone
              email
              registrationOpenAt
              registrationCloseAt
              checkInStartsAt
              checkInEndsAt
              assistantTournamentDirectorName
              assistantTournamentDirectorPhone
              assistantTournamentDirectorEmail
              website
              isVerifyMode
              isResultSigningEnabled
              multipleScorecardModeEnabled
              pdgaEventId
              startDate
              maxWildcards
              infoSections {
                id
                title
                content
              }
              tourInfoSections {
                id
                title
                content
              }
              pdgaType{
                type
                name
              }
              city{
                name
                country
                geolocation{
                  lat
                  lng
                }
              }
              divisionInPool {
                  poolId
                  divisionId
              }
              games{
                id
                type
                rounds{
                  status
                  id
                  date
                }
              }
              rounds{
                id
                pools{
                  id
                  name
                  date
                  endDate
                  status
                  startMethod
                  areGroupsPublic
                  playFormat
                  layoutVersion{
                    layout{
                      id
                      name
                      course{
                        id
                        name
                        type
                      }
                    }
                  }
                }
              }
              registrationStages{
                id
                date
                criterias{
                  id
                  type
                  value
                }
                division{
                  id
                }
              }
              divisions{
                id
                name
                type
                maxRegistrations
                maxSpots
                reservedSpots
                refundPercentage
                minRating
                maxRating
                registrationFee{
                  value
                  formatted
                  currency
                }
                tourPassFee{
                  value
                  formatted
                  currency
                }
                tourPasses{
                  id
                }
              }
              registrationStages{
                id
                date
                criterias{
                  id
                  type
                  value
                }
                division{
                  id
                }
              }
              documents{
                id
                url
                documentType
                displayName
              }
              sponsors{
                id
                displayName
                url
                imageUrl
              }
              tour{
                id
                name
                type{
                  type
                  name
                }
                events{
                  id
                }
                organizations{
                  id
                  name
                  slug
                  type
                  logoUrl
                }
                sponsors{
                  id
                  displayName
                  url
                  imageUrl
                }
              }
            }
          }
          `,
        },
      })

      this.eventData = eventData.data.data.event
      this.loaded = true

      this.checkTruncation()
    },
  },
  beforeMount() {
    window.addEventListener('resize', this.checkTruncation)
    this.loadData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkTruncation)
  },
  destroyed() {},
}
</script>
<style lang="scss">
.about-container {
  .info-sections-list {
    margin-top: 2rem;
    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 0;
      border-top: 1px dashed $fog;
      gap: 12px;
      cursor: pointer;
      p {
        font-size: 1rem;
        line-height: 1.5rem;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }
  }
}
.event-description-preview {
  a {
    color: $ocean;
  }
  a,
  ol,
  ul,
  li,
  p,
  b {
    overflow-wrap: anywhere;
  }
}
.read-more-container {
  p {
    margin: 0;
  }
}
.event-description-preview-container {
  margin-top: 12px;
  position: relative;
  max-height: 400px;
  overflow: hidden;

  &.is-truncated {
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 40px;
      background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 1) 0,
        rgba(0, 0, 0, 0) 100%
      );
    }
  }
}
.event-description-preview {
  p {
    margin: 0;
  }
}
</style>
<style lang="scss" scoped>
@import '@/styles/Typography';

.event-count {
  margin-top: 16px;
  font-size: 16px;
}

.cost-label {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid $midnight;

  p {
    font-size: 14px;
  }

  &.free {
    border: 1px solid $grass;
    color: $grass;
  }
}

.tour-pass {
  svg {
    width: 40px;
    height: 40px;

    &.disabled {
      opacity: 0.3;
    }
  }
}

.division-name {
  font-size: 18px;
  width: 100%;
}

.division {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  justify-content: space-between;
  padding: 18px 20px;
  cursor: pointer;
  border-radius: 15px;
  box-shadow: 0px 0px 10px #00000019;

  &:hover {
    box-shadow: 0px 0px 10px #00000033;
  }
}

.divisions {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.td-wrapper {
  &.assistant {
    margin-top: 24px;
  }

  .td-title {
    font-size: 1.125rem;
  }

  .td-name {
    margin-top: 12px;
    font-size: 1rem;
  }

  .td-phone,
  .td-email {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
    }

    p {
      font-size: 1rem;
    }
  }
}

.event-home-container {
  padding: 0 12px;
  /* Add some padding to the container */
  display: grid;
  grid-template-columns: 1fr;
  /* Single column by default */
  gap: 0 16px;
  /* Add some space between the items */

  > div:first-of-type {
    margin-top: 0;
  }
}

.read-more {
  margin-top: 16px;
  font-size: 16px;
  color: $ocean;
  cursor: pointer;
}

.read-more-container {
  padding: 0 24px 24px 24px;
  min-width: 300px;
  max-width: 425px;

  .section-title {
    font-size: 20px;
  }

  .event-description {
    margin-top: 12px;
    a {
      color: $ocean;
    }
  }
}

.section-title {
  font-size: 20px;
}

.labels {
  display: flex;
  gap: 8px;
  margin-top: 20px;

  span {
    font-size: 12px;
    line-height: 12px;
    padding: 4px 6px;
    border-radius: 4px;
    border: 1px solid $midnight;

    &.doubles {
      border: 1px solid #cceefe;
      color: $sky;
      background: #cceefe;
    }

    &.tour-type {
      border: 1px solid #ffe2da;
      color: $sunset;
      background: #ffe2da;
    }

    &.pdga-tier {
      border: 1px solid #cdf0f2;
      color: $ocean;
      background: #cdf0f2;
    }

    &.max-wildcards {
      border: 1px solid #6c4827;
      color: #e6daa2;
      background: #6c4827;
    }

    &.distance {
      border: 1px solid #fff0cc;
      color: $sunrise;
      background: #fff0cc;
    }
  }
}

.about-container {
  padding-bottom: 40px;
  border-bottom: 1px solid $sleet;
}

.schedule-container {
  margin-top: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid $sleet;
}

.location-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid $sleet;

  span {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  p {
    line-height: 24px;
    font-size: 16px;
  }
}

.td-container {
  margin-top: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid $sleet;
}

.website-pdga-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid $sleet;

  a {
    font-size: 16px;
  }
}

.divisions-container {
  flex-wrap: wrap;

  .division-details-wrapper {
    padding-top: 20px;
    margin-top: 8px;
    border-top: 1px solid $sleet;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;

    span {
      display: block;
      width: 4px;
      height: 4px;
      background: $midnight;
      border-radius: 50%;
    }

    p {
      font-size: 1rem;
    }

    .refund {
      display: flex;
      width: 100%;
      align-items: center;
      gap: 6px;

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .learn-more {
      cursor: pointer;
      color: $ocean;
    }
  }
}

.documents-container {
  padding-top: 40px;
  margin-top: 40px;
  border-top: 1px solid $sleet;
}

.documents-wrapper {
  margin-top: 16px;
}

.document {
  border-radius: 15px;
  box-shadow: 0px 0px 10px #00000019;
  display: grid;
  grid-template-columns: 1fr 32px;
  padding: 18px 20px;
  gap: 6px;

  .document-name {
    font-size: 16px;
  }

  .document-type {
    font-size: 16px;
    text-transform: uppercase;
    grid-column: 1;
    color: $strom;
  }

  .download {
    cursor: pointer;
    grid-column: 2;
    grid-row: 1 / 3;
    align-content: center;
    justify-self: end;
  }
}

.scoring-method-container {
  margin-top: 40px;
  padding-top: 40px;
  border-top: 1px solid $sleet;
}

.scoring-method-wrapper {
  margin-top: 16px;
}

.scoring-method-title {
  display: flex;
  align-items: center;
  gap: 8px;

  p {
    font-size: 16px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
}

.scoring-method-copy {
  margin-top: 8px;
  font-size: 16px;
  line-height: 1.5rem;
  color: $strom;
}

.sponsors-container {
  margin-top: 40px;
  padding-top: 40px;
  border-top: 1px solid $sleet;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  .section-title {
    margin-bottom: 0;
    grid-column: 1 / 3;
  }

  a {
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 16px;
    border-radius: 10px;
    border: 1px solid $cloud;
    box-shadow: 0px 0px 10px #00000019;
    padding: 1.5rem;
    justify-content: center;
    transition: all ease 0.3s;

    &:hover {
      box-shadow: 0px 0px 10px #00000033;
    }
    img {
      max-width: 100%;
      max-height: 4.5rem;
    }
  }
}

.logo-placeholder {
  width: 100%;
  height: 8rem;
  background: $fog;
  border-radius: 8px;
}

.organizers-container {
  margin-top: 40px;
  border-top: 1px solid $sleet;
  padding-top: 40px;
}

.organizer-wrapper {
  margin-top: 12px;
  border-radius: 15px;
  box-shadow: 0px 0px 10px #00000019;
  display: grid;
  grid-template-columns: auto 1fr;
  padding: 18px 20px;
  gap: 4px 16px;
  max-width: 425px;

  p {
    margin-top: 8px;
    font-size: 18px;
  }
}

.organizer-logo,
.organizer-logo-placeholder {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  grid-row: 1 / 3;
  background-color: $fog;
}

.organizer-label {
  padding: 5px 8px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 12px;
  justify-self: start;
  align-self: flex-start;

  &.club {
    background: $sunrise;
    color: white;
  }

  &.promoter {
    background: $gold;
    color: white;
  }

  &.association {
    background: $sunset;
    color: white;
  }
}

.divisions,
.documents-container {
  .section-title {
    margin-bottom: 8px;
  }
}

.organizers-container {
  .section-title {
    margin-bottom: 16px;
  }
}

@media (min-width: 480px) and (orientation: landscape) {
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
  .about-container {
    .info-sections-list {
      li {
        padding: 1rem 0;
        gap: 1rem;

        svg {
          height: 1rem;
        }
      }
    }
  }
  .divisions {
    margin-top: 0;
  }

  .organizers-container {
    margin-top: 64px;
  }

  .divisions,
  .documents-container,
  .sponsors-container,
  .organizers-container {
    .section-title {
      margin-bottom: 8px;
    }
  }

  .scoring-method-wrapper:nth-child(n + 3) {
    margin-top: 32px;
  }

  .scoring-method-title {
    gap: 12px;
    b {
      font-size: 18px;
    }

    svg {
      width: 32px;
      height: 32px;
    }
  }

  .scoring-method-copy {
    margin-top: 16px;
  }

  .event-home-container {
    grid-template-columns: 3fr 2fr;
    /* Two columns: 2/3 for main content, 1/3 for sidebar */
    grid-template-rows: auto;
    /* Adjust rows automatically based on content */
    column-gap: 60px;

    > div:nth-child(2) {
      margin-top: 0;
    }
  }

  .schedule-container {
    grid-column: 2 / 3;
    /* Schedule container in the second column */
    grid-row: 1 / 9;
    /* Span all rows */
    padding-bottom: 0;
    border-bottom: 0;
  }

  .about-container,
  .location-container,
  .td-container,
  .website-pdga-container,
  .divisions-container,
  .documents-container,
  .scoring-method-container,
  .sponsors-container,
  .organizers-container {
    grid-column: 1 / 2;
    /* Main content in the first column */
  }
}

@media (min-width: 1200px) {
  .location-container {
    flex-direction: row;
    gap: 2rem;
  }
  .event-count {
    margin-top: 24px;
  }

  .scoring-method-container,
  .documents-container {
    margin-top: 64px;
    padding-top: 64px;
  }
  .divisions-container {
    margin-top: 64px;
  }

  .sponsors-container {
    a {
      padding: 2.5rem;

      img {
        max-height: 3.5rem;
      }
    }
    .section-title {
      grid-column: 1 / 4;
    }
    padding-top: 64px;
    margin-top: 64px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }

  .section-title {
    font-size: 24px;
  }

  .documents-wrapper {
    margin-top: 0;
    width: 100%;
  }

  .divisions,
  .documents-container,
  .scoring-method-container {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr 1fr;

    .section-title {
      grid-column: 1 / 3;
    }
  }

  .scoring-method-container {
    gap: 24px 64px;
  }

  .td-container {
    display: flex;
    justify-content: space-between;
    gap: 32px;

    .td-wrapper {
      flex: 1;

      .td-title {
        font-size: 20px;
      }

      &.assistant {
        margin-top: 0;
      }
    }
  }

  .read-more-container {
    padding: 0 32px 32px 32px;
    max-width: 700px;

    .section-title {
      font-size: 24px;
    }

    .event-description {
      margin-top: 16px;
    }
  }

  .event-home-container {
    grid-template-columns: 5fr 3fr;
    /* Two columns: 2/3 for main content, 1/3 for sidebar */
    grid-template-rows: auto;
    /* Adjust rows automatically based on content */
    column-gap: 120px;
    padding: 0;
  }

  .schedule-container {
    grid-column: 2 / 3;
    /* Schedule container in the second column */
    margin-top: 0;
  }

  .scoring-method-wrapper {
    margin-top: 0;

    &:nth-child(n + 3) {
      margin-top: 0;
    }
  }

  .document {
    transition: all ease 0.3s;
    cursor: pointer;

    &:hover {
      box-shadow: 0 0 10px #00000039;
    }
  }

  .organizer-wrapper {
    margin-top: 24px;
    cursor: pointer;
    transition: all ease 0.3s;

    &:hover {
      box-shadow: 0 0 10px #00000039;
    }
  }

  .website-pdga-container {
    justify-content: flex-start;
    gap: 64px;
  }
}
</style>
